import { useCallback, useMemo } from "react";
import {
  AllCapabilities,
  AllRolePermissions,
  Capabilities,
  CapabilitiesWithVenueGroupId,
  CapabilitiesWithVenueId,
  CapabilityHelpers,
  DEFAULT_ROLE_PERMISSIONS,
  DerivedCapabilities,
  DerivedRoles,
  RoleHelpers,
  RolePermissions,
  UsePermissionsReturn
} from "../types/permissions.types";
import { useAppContext } from "./useAppContext";
import { useDiaryContext } from "./useDiaryContext";

export const usePermissions = (): UsePermissionsReturn => {
  const { venueUserConfig: user, venueGroupConfig } = useAppContext();
  const { venueConfig, venues, isInitialising } = useDiaryContext();
  const loading =
    !user?._id ||
    !venueGroupConfig?._id ||
    (venues?.length === 0 ? isInitialising : !venues || !venueConfig?._id);
  // ============================================================================
  // Roles
  // ============================================================================

  const userRoles = useMemo(() => user?.roles ?? null, [user]);

  const rolePermissions: string[] = useMemo(() => {
    if (!user || !userRoles) {
      return DEFAULT_ROLE_PERMISSIONS;
    }

    const rolePermissions = userRoles.permissions ?? [];
    return [...DEFAULT_ROLE_PERMISSIONS, ...rolePermissions];
  }, [user, userRoles]);

  const hasRolePermission = useCallback(
    (permission: RolePermissions): boolean =>
      rolePermissions.includes(permission),
    [rolePermissions]
  );

  const roles: AllRolePermissions & RoleHelpers & DerivedRoles = useMemo(() => {
    const helpers: RoleHelpers = {
      hasRolePermission
    };

    const permissions: AllRolePermissions = {
      auditLog: {
        read: hasRolePermission("audit_log.read"),
        download: hasRolePermission("audit_log.download")
      },
      customer: {
        create: hasRolePermission("customer.create"),
        read: hasRolePermission("customer.read"),
        update: hasRolePermission("customer.update"),
        delete: hasRolePermission("customer.delete"),
        download: hasRolePermission("customer.download")
      },
      site: {
        create: hasRolePermission("site.create"),
        update: hasRolePermission("site.update"),
        delete: hasRolePermission("site.delete"),
        approveChanges: {
          update: hasRolePermission("site.approve_changes.update")
        },
        content: {
          update: hasRolePermission("site.content.update"),
          contribute: hasRolePermission("site.content.contribute"),
          read: hasRolePermission("site.content.read"),
          internal: hasRolePermission("site.content.internal")
        },
        areas: {
          update: hasRolePermission("site.areas.update")
        },
        autoDoubleConfirm: {
          update: hasRolePermission("site.auto_double_confirm.update")
        },
        availability: {
          update: hasRolePermission("site.availability.update")
        },
        bookingRequirements: {
          update: hasRolePermission("site.booking_requirements.update")
        },
        bookingTypePriority: {
          read: hasRolePermission("site.booking_type_priority.read"),
          update: hasRolePermission("site.booking_type_priority.update")
        },
        concierge: {
          read: hasRolePermission("site.concierge.read"),
          update: hasRolePermission("site.concierge.update")
        },
        dailyNotes: {
          read: hasRolePermission("site.daily_notes.read"),
          update: hasRolePermission("site.daily_notes.update")
        },
        diarySettings: {
          read: hasRolePermission("site.diary_settings.read"),
          update: hasRolePermission("site.diary_settings.update")
        },
        documents: {
          read: hasRolePermission("site.documents.read"),
          update: hasRolePermission("site.documents.update")
        },
        financialDetails: {
          read: hasRolePermission("site.financial_details.read"),
          update: hasRolePermission("site.financial_details.update")
        },
        floorplans: {
          read: hasRolePermission("site.floorplans.read"),
          update: hasRolePermission("site.floorplans.update")
        },
        integrationConfigs: {
          read: hasRolePermission("site.integration_configs.read"),
          update: hasRolePermission("site.integration_configs.update")
        },
        internal: {
          create: hasRolePermission("site.internal.create"),
          delete: hasRolePermission("site.internal.delete"),
          read: hasRolePermission("site.internal.read"),
          update: hasRolePermission("site.internal.update")
        },
        invoicingAndContracts: {
          read: hasRolePermission("site.invoicing_and_contracts.read"),
          update: hasRolePermission("site.invoicing_and_contracts.update")
        },
        labelsAndColours: {
          read: hasRolePermission("site.labels_and_colours.read"),
          update: hasRolePermission("site.labels_and_colours.update")
        },
        linkedBookings: {
          create: hasRolePermission("site.linked_bookings.create"),
          delete: hasRolePermission("site.linked_bookings.delete"),
          update: hasRolePermission("site.linked_bookings.update")
        },
        marketingSettings: {
          read: hasRolePermission("site.marketing_settings.read"),
          update: hasRolePermission("site.marketing_settings.update")
        },
        messaging: {
          read: hasRolePermission("site.messaging.read"),
          update: hasRolePermission("site.messaging.update")
        },
        notificationSettings: {
          read: hasRolePermission("site.notification_settings.read"),
          update: hasRolePermission("site.notification_settings.update")
        },
        payments: {
          read: hasRolePermission("site.payments.read"),
          update: hasRolePermission("site.payments.update")
        },
        preorders: {
          update: hasRolePermission("site.preorders.update")
        },
        reporting: {
          read: hasRolePermission("site.reporting.read"),
          update: hasRolePermission("site.reporting.update")
        },
        venueHireSettings: {
          read: hasRolePermission("site.venue_hire_settings.read"),
          update: hasRolePermission("site.venue_hire_settings.update")
        },
        waitlistSettings: {
          read: hasRolePermission("site.waitlist_settings.read"),
          update: hasRolePermission("site.waitlist_settings.update")
        }
      },
      organisation: {
        create: hasRolePermission("organisation.create"),
        delete: hasRolePermission("organisation.delete"),
        read: hasRolePermission("organisation.read"),
        update: hasRolePermission("organisation.update"),
        content: {
          read: hasRolePermission("organisation.content.read"),
          update: hasRolePermission("organisation.content.update")
        },
        bookingRequirements: {
          update: hasRolePermission("organisation.booking_requirements.update")
        },
        bookingSettings: {
          read: hasRolePermission("organisation.booking_settings.read"),
          update: hasRolePermission("organisation.booking_settings.update")
        },
        financialDetails: {
          read: hasRolePermission("organisation.financial_details.read"),
          update: hasRolePermission("organisation.financial_details.update")
        },
        integrationConfigs: {
          create: hasRolePermission("organisation.integration_configs.create"),
          delete: hasRolePermission("organisation.integration_configs.delete"),
          read: hasRolePermission("organisation.integration_configs.read"),
          update: hasRolePermission("organisation.integration_configs.update")
        },
        internal: {
          read: hasRolePermission("organisation.internal.read"),
          update: hasRolePermission("organisation.internal.update")
        },
        invoicingAndContracts: {
          read: hasRolePermission("organisation.invoicing_and_contracts.read"),
          update: hasRolePermission(
            "organisation.invoicing_and_contracts.update"
          )
        },
        labelsAndColours: {
          read: hasRolePermission("organisation.labels_and_colours.read"),
          update: hasRolePermission("organisation.labels_and_colours.update")
        },
        linkedBookings: {
          read: hasRolePermission("organisation.linked_bookings.read"),
          update: hasRolePermission("organisation.linked_bookings.update")
        },
        marketingSettings: {
          read: hasRolePermission("organisation.marketing_settings.read"),
          update: hasRolePermission("organisation.marketing_settings.update")
        },
        messaging: {
          read: hasRolePermission("organisation.messaging.read"),
          update: hasRolePermission("organisation.messaging.update")
        },
        notificationSettings: {
          read: hasRolePermission("organisation.notification_settings.read"),
          update: hasRolePermission("organisation.notification_settings.update")
        },
        payments: {
          read: hasRolePermission("organisation.payments.read"),
          update: hasRolePermission("organisation.payments.update")
        },
        preorders: {
          read: hasRolePermission("organisation.preorders.read"),
          update: hasRolePermission("organisation.preorders.update")
        },
        reporting: {
          read: hasRolePermission("organisation.reporting.read"),
          update: hasRolePermission("organisation.reporting.update")
        },
        siteTags: {
          update: hasRolePermission("organisation.site_tags.update")
        }
      },
      booking: {
        create: hasRolePermission("booking.create"),
        read: hasRolePermission("booking.read"),
        update: hasRolePermission("booking.update"),
        delete: hasRolePermission("booking.delete"),
        download: hasRolePermission("booking.download"),
        areas: {
          read: hasRolePermission("booking.areas.read"),
          update: hasRolePermission("booking.areas.update")
        },
        contract: {
          read: hasRolePermission("booking.contract.read"),
          update: hasRolePermission("booking.contract.update")
        },
        deposits: {
          create: hasRolePermission("booking.deposits.create"),
          download: hasRolePermission("booking.deposits.download"),
          read: hasRolePermission("booking.deposits.read"),
          update: hasRolePermission("booking.deposits.update")
        },
        financialDetails: {
          read: hasRolePermission("booking.financial_details.read"),
          update: hasRolePermission("booking.financial_details.update")
        },
        integrationConfigs: {
          read: hasRolePermission("booking.integration_configs.read"),
          update: hasRolePermission("booking.integration_configs.update")
        },
        internal: {
          read: hasRolePermission("booking.internal.read"),
          update: hasRolePermission("booking.internal.update")
        },
        invoicing: {
          read: hasRolePermission("booking.invoicing.read"),
          update: hasRolePermission("booking.invoicing.update")
        },
        messages: {
          read: hasRolePermission("booking.messages.read"),
          create: hasRolePermission("booking.messages.create")
        },
        notes: {
          read: hasRolePermission("booking.notes.read"),
          update: hasRolePermission("booking.notes.update")
        },
        preorders: {
          read: hasRolePermission("booking.preorders.read"),
          update: hasRolePermission("booking.preorders.update"),
          download: hasRolePermission("booking.preorders.download")
        },
        refunds: {
          create: hasRolePermission("booking.refunds.create"),
          read: hasRolePermission("booking.refunds.read")
        }
      },
      bookingType: {
        update: hasRolePermission("booking_type.update"),
        areas: {
          update: hasRolePermission("booking_type.areas.update")
        },
        autoConfirmRules: {
          update: hasRolePermission("booking_type.auto_confirm_rules.update")
        },
        availability: {
          update: hasRolePermission("booking_type.availability.update")
        },
        customerChanges: {
          read: hasRolePermission("booking_type.customer_changes.read"),
          update: hasRolePermission("booking_type.customer_changes.update")
        },
        messaging: {
          read: hasRolePermission("booking_type.messaging.read"),
          update: hasRolePermission("booking_type.messaging.update")
        },
        payments: {
          update: hasRolePermission("booking_type.payments.update")
        },
        preorders: {
          update: hasRolePermission("booking_type.preorders.update")
        },
        prompts: {
          read: hasRolePermission("booking_type.prompts.read"),
          update: hasRolePermission("booking_type.prompts.update")
        }
      },
      user: {
        create: hasRolePermission("user.create"),
        read: hasRolePermission("user.read"),
        update: hasRolePermission("user.update"),
        delete: hasRolePermission("user.delete"),
        financialDetails: {
          read: hasRolePermission("user.financial_details.read"),
          update: hasRolePermission("user.financial_details.update")
        },
        internal: {
          read: hasRolePermission("user.internal.read"),
          update: hasRolePermission("user.internal.update")
        },
        marketingSettings: {
          read: hasRolePermission("user.marketing_settings.read")
        },
        permissions: {
          read: hasRolePermission("user.permissions.read"),
          update: hasRolePermission("user.permissions.update")
        },
        workspaceDetails: {
          read: hasRolePermission("user.workspace_details.read"),
          update: hasRolePermission("user.workspace_details.update")
        }
      },
      bankTransfer: {
        read: hasRolePermission("bank_transfer.read"),
        download: hasRolePermission("bank_transfer.download")
      },
      payment: {
        read: hasRolePermission("payment.read"),
        download: hasRolePermission("payment.download")
      },
      collins: {
        manage: hasRolePermission("collins.manage")
      }
    };

    const derived: DerivedRoles = {
      derived: {
        hasRoles: userRoles !== null,
        userIsInternal:
          permissions.collins.manage || hasRolePermission("collins.update")
      }
    };

    return { ...permissions, ...helpers, ...derived };
  }, [hasRolePermission, userRoles]);

  // ============================================================================
  // CAPABILITIES
  // ============================================================================

  const userCapabilities = user?.capabilities;
  const hasCapabilityPermission = useCallback(
    (capability: Capabilities) => {
      if (!userCapabilities) {
        return false;
      }

      // Direct capability check
      return userCapabilities.includes(capability);
    },
    [userCapabilities]
  );

  const extractIdFromCapability = useCallback((capability: string): string => {
    const parts = capability.split("_");
    return parts[parts.length - 1];
  }, []);

  const getIdsFromCapabilities = useCallback(
    (requiredCapabilities: Capabilities[]): string[] => {
      if (!userCapabilities) {
        return [];
      }

      const ids: string[] = [];

      userCapabilities.forEach((capability) => {
        requiredCapabilities.forEach((required) => {
          if (capability.startsWith(required)) {
            ids.push(extractIdFromCapability(capability));
          }
        });
      });

      return Array.from(new Set(ids))
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b));
    },
    [userCapabilities, extractIdFromCapability]
  );

  // Helper functions for creating capability objects with venue/venue group IDs
  const getVenueIds = useCallback(
    (capabilityPrefix: CapabilitiesWithVenueId) => {
      if (!userCapabilities) {
        return {
          ids: [],
          some: false,
          thisVenue: false,
          hasId: () => false
        };
      }

      const ids: string[] = [];
      userCapabilities.forEach((capability) => {
        if (capability.startsWith(capabilityPrefix)) {
          ids.push(extractIdFromCapability(capability));
        }
      });

      const uniqueIds = Array.from(new Set(ids))
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b));
      const currentVenueId = venueConfig?._id?.toString();

      return {
        ids: uniqueIds,
        some: uniqueIds.length > 0,
        thisVenue: currentVenueId ? uniqueIds.includes(currentVenueId) : false,
        hasId: (id: string) => uniqueIds.includes(id)
      };
    },
    [userCapabilities, extractIdFromCapability, venueConfig?._id]
  );

  const getVenueGroupIds = useCallback(
    (capabilityPrefix: CapabilitiesWithVenueGroupId) => {
      if (!userCapabilities) {
        return {
          ids: [],
          some: false,
          thisVenueGroup: false,
          hasId: () => false
        };
      }

      const ids: string[] = [];
      userCapabilities.forEach((capability) => {
        if (capability.startsWith(capabilityPrefix)) {
          ids.push(extractIdFromCapability(capability));
        }
      });

      const uniqueIds = Array.from(new Set(ids))
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b));
      const currentVenueGroupId = venueGroupConfig?._id?.toString();

      return {
        ids: uniqueIds,
        some: uniqueIds.length > 0,
        thisVenueGroup: currentVenueGroupId
          ? uniqueIds.includes(currentVenueGroupId)
          : false,
        hasId: (id: string) => uniqueIds.includes(id)
      };
    },
    [userCapabilities, extractIdFromCapability, venueGroupConfig?._id]
  );

  const capabilities: AllCapabilities &
    CapabilityHelpers &
    DerivedCapabilities = useMemo(() => {
    const helpers: CapabilityHelpers = {
      hasCapabilityPermission,
      getIdsFromCapabilities
    };
    const permissions: AllCapabilities = {
      // Access & Authentication
      accessAuditLog: hasCapabilityPermission("access_audit_log"),
      accessCollinsSuccess: hasCapabilityPermission("access_collins_success"),
      hasEarlyAccessPreview: hasCapabilityPermission(
        "has_early_access_preview"
      ),
      evoEarlyAdopter: hasCapabilityPermission("evo_early_adopter"),

      // Booking Management
      accessVenueBookings: getVenueIds("access_venue_bookings_"),
      accessVenueGroupBookings: getVenueGroupIds(
        "access_venue_group_bookings_"
      ),
      confirmVenueBookings: getVenueIds("confirm_venue_bookings_"),
      confirmVenueGroupBookings: getVenueGroupIds(
        "confirm_venue_group_bookings_"
      ),
      deleteVenueBookings: getVenueIds("delete_venue_bookings_"),
      deleteVenueGroupBookings: getVenueGroupIds(
        "delete_venue_group_bookings_"
      ),
      manageBookings: hasCapabilityPermission("manage_bookings"),
      manageDmnSourceBookings: hasCapabilityPermission(
        "manage_dmn_source_bookings"
      ),
      manageNoVenueBookings: getVenueGroupIds("manage_no_venue_bookings_"),
      manageVenueBookings: getVenueIds("manage_venue_bookings_"),
      manageVenueGroupBookings: getVenueGroupIds(
        "manage_venue_group_bookings_"
      ),
      readAllBookings: hasCapabilityPermission("read_all_bookings"),
      viewDmnReferralSourceBookings: hasCapabilityPermission(
        "view_dmn_referral_source_bookings"
      ),

      // Customer Management
      downloadVenueCustomers: getVenueIds("download_venue_customers_"),
      downloadVenueGroupCustomers: getVenueGroupIds(
        "download_venue_group_customers_"
      ),
      manageVenueCustomers: getVenueIds("manage_venue_customers_"),
      readAllCustomers: hasCapabilityPermission("read_all_customers"),
      viewVenueCustomers: getVenueIds("view_venue_customers_"),
      viewVenueGroupCustomers: getVenueGroupIds("view_venue_group_customers_"),

      // Content & Pages
      editContent: hasCapabilityPermission("edit_content"),

      // Financial & Banking
      manageVenueAccounts: hasCapabilityPermission("manage_venue_accounts"),
      manageVenueBankDetails: hasCapabilityPermission(
        "manage_venue_bank_details"
      ),
      manageVenueGroupRefunds: getVenueGroupIds("manage_venue_group_refunds_"),
      manageVenueRefunds: getVenueIds("manage_venue_refunds_"),
      readBankTransferRecords: hasCapabilityPermission(
        "read_bank_transfer_records"
      ),

      // System Administration
      impersonateUsers: hasCapabilityPermission("impersonate_users"),
      manageCollins: hasCapabilityPermission("manage_collins"),
      managePermissions: hasCapabilityPermission("manage_permissions"),
      manageRestrictedPermissions: hasCapabilityPermission(
        "manage_restricted_permissions"
      ),
      manageUsers: hasCapabilityPermission("manage_users"),
      viewAdminAudit: hasCapabilityPermission("view_admin_audit"),
      manageWebhookSubscriptions: hasCapabilityPermission(
        "manage_webhook_subscriptions"
      ),

      // Venue & Group Management
      manageVenueBookingRules: getVenueIds("manage_venue_booking_rules_"),
      manageVenueGroupBookingRules: getVenueGroupIds(
        "manage_venue_group_booking_rules_"
      ),
      manageVenueGroupPermissions: getVenueGroupIds(
        "manage_venue_group_permissions_"
      ),
      manageVenueGroupVenues: getVenueGroupIds("manage_venue_group_venues_"),
      manageVenuePermissions: getVenueIds("manage_venue_permissions_"),
      readAllVenues: hasCapabilityPermission("read_all_venues"),

      // Reports & Analytics
      downloadVenueGroupReports: getVenueGroupIds(
        "download_venue_group_reports_"
      ),
      downloadVenueReports: getVenueIds("download_venue_reports_"),
      downloadVenueReportsInternal: hasCapabilityPermission(
        "download_venue_reports_internal"
      ),
      viewStats: hasCapabilityPermission("view_stats"),
      viewVenueGroupReports: getVenueGroupIds("view_venue_group_reports_"),
      viewVenueReports: getVenueIds("view_venue_reports_"),
      viewVenueReportsInternal: hasCapabilityPermission(
        "view_venue_reports_internal"
      )
    };

    const derived: DerivedCapabilities = {
      derived: {
        userIsInternal:
          permissions.manageBookings ||
          permissions.manageDmnSourceBookings ||
          permissions.manageCollins ||
          permissions.editContent
      }
    };

    return { ...permissions, ...helpers, ...derived };
  }, [
    hasCapabilityPermission,
    getIdsFromCapabilities,
    getVenueIds,
    getVenueGroupIds
  ]);

  const response = useMemo(() => {
    // Check if this is a workspace instance (loaded in an iframe)
    const isWorkspaceInstance =
      window.parent && window.location !== window.parent.location;

    // Check if user is DMN employee
    const userIsDmn = Boolean(user?.email?.match(/@designmynight\.com$/));

    return {
      roles,
      capabilities,
      hasVenues: (venues ?? []).length > 0,
      isWorkspaceInstance,
      userIsDmn,
      loading
    };
  }, [roles, capabilities, venues, user?.email, loading]);

  return response;
};

export default usePermissions;
